import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ObjectException } from 'global/errors';
import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import { isNotValidObjectID } from 'functions/stringValidation';
import services from 'api/services';

const { ConfigService, FreecodeService } = services;

export default function useCreatorLoadFunction(testId) {
  const { isInitialized } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);

  const { setTest, setTestImpl, setLanguageData } = useFreecodeContextData();
  const { setCodeSol } = useFreecodeContextRun();

  useEffect(() => {
    if (!isInitialized || !testId) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(testId)) throw new ObjectException(`Not a valid id ${testId}`, 'VALIDATION_ERROR');

        const [languageVersions, response] = await Promise.all([
          ConfigService.getLanguageVersions(),
          FreecodeService.loadFreecodeCreator(testId),
        ]);
        const { test, testImpl } = response;

        if (!test?._id) throw new ObjectException('Test missing from response', 'VALIDATION_ERROR');
        if (test?._id !== testImpl?.test)
          throw new ObjectException('Test and implementation misssmatch', 'VALIDATION_ERROR');

        let allowedLanguages = languageVersions?.value?.[testImpl?.executionVersion];
        if (!allowedLanguages?.python) throw new ObjectException('Language versions missing', 'VALIDATION_ERROR');

        setTest(test);
        setTestImpl(testImpl);
        setLanguageData(allowedLanguages);
        setCodeSol(testImpl.solution || testImpl.generated?.skeletons?.python || 'Code not found...');

        setLoading(false);
      } catch (err) {
        setError(err);
      }
    };
    fetchData();
  }, [testId, isInitialized, setCodeSol, setLanguageData, setTest, setTestImpl]);

  return { loading, error };
}
